import { useState, useEffect, useCallback } from "react";

// Simple in-memory cache with TTL support
class CacheManager {
  constructor() {
    this.cache = new Map();
    this.timers = new Map();
  }

  set(key, value, ttl = 300000) {
    // Default 5 minutes TTL
    // Clear existing timer if any
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
    }

    // Set the value
    this.cache.set(key, {
      value,
      timestamp: Date.now(),
      ttl,
    });

    // Set expiration timer
    const timer = setTimeout(() => {
      this.delete(key);
    }, ttl);

    this.timers.set(key, timer);
  }

  get(key) {
    const item = this.cache.get(key);

    if (!item) {
      return null;
    }

    // Check if expired
    if (Date.now() - item.timestamp > item.ttl) {
      this.delete(key);
      return null;
    }

    return item.value;
  }

  delete(key) {
    this.cache.delete(key);

    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
    }
  }

  clear(prefix) {
    if (prefix) {
      Array.from(this.cache.keys())
        .filter((key) => key.startsWith(prefix))
        .forEach((key) => this.delete(key));
      return;
    }

    this.timers.forEach((timer) => clearTimeout(timer));
    this.cache.clear();
    this.timers.clear();
  }

  has(key) {
    return this.cache.has(key) && this.get(key) !== null;
  }

  size() {
    return this.cache.size;
  }
}

// Global cache instance
const globalCache = new CacheManager();

// Hook for using cache with async operations
export const useCache = (key, fetcher, options = {}) => {
  const {
    ttl = 300000, // 5 minutes default
    enabled = true,
    staleWhileRevalidate = false,
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      if (!enabled) return;

      // Check cache first (unless forcing refresh)
      if (!forceRefresh && globalCache.has(key)) {
        const cachedData = globalCache.get(key);
        setData(cachedData);
        return cachedData;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await fetcher();

        // Cache the result
        globalCache.set(key, result, ttl);
        setData(result);
        setLoading(false);

        return result;
      } catch (err) {
        setError(err);
        setLoading(false);
        throw err;
      }
    },
    [key, fetcher, ttl, enabled]
  );

  const mutate = useCallback(
    (newData) => {
      if (newData !== undefined) {
        globalCache.set(key, newData, ttl);
        setData(newData);
      } else {
        // Revalidate
        return fetchData(true);
      }
    },
    [key, ttl, fetchData]
  );

  const invalidate = useCallback(() => {
    globalCache.delete(key);
    setData(null);
  }, [key]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    mutate,
    invalidate,
    refetch: () => fetchData(true),
  };
};

// Hook for simple value caching
export const useCacheValue = (key, value, ttl = 300000) => {
  useEffect(() => {
    if (value !== undefined && value !== null) {
      globalCache.set(key, value, ttl);
    }
  }, [key, value, ttl]);

  const getCachedValue = useCallback(() => {
    return globalCache.get(key);
  }, [key]);

  const setCachedValue = useCallback(
    (newValue) => {
      globalCache.set(key, newValue, ttl);
    },
    [key, ttl]
  );

  const deleteCachedValue = useCallback(() => {
    globalCache.delete(key);
  }, [key]);

  return {
    getCachedValue,
    setCachedValue,
    deleteCachedValue,
  };
};

// Export cache manager for direct access
export { globalCache };

export default useCache;
